<template>
  <avs-card>
    <template v-slot:title>
      <v-card-title>{{ $t("categories.nonconformity.details") }}</v-card-title>
    </template>
    <template v-slot:content>
      <v-card-text>
        <div class="d-flex justify-end mr-2">
          <avs-switch
            mdi1="mdi-chart-bar"
            mdi2="mdi-chart-pie"
            :spantext="chartType.tooltip"
            @switch="v => onChartTypeChange(v)"
          />
        </div>
        <div class="d-flex justify-space-around">
          <v-col cols="4">
            <div ref="polarAreaChart"></div>
            <v-select
              class="mt-5"
              dense
              :items="inspections"
              item-text="inspection"
              :label="$tc('categories.inspection.name', 1)"
              outlined
              return-object
              :value="selectedInspection"
              @change="onSelectedInspectionChange"
            ></v-select>
          </v-col>
          <v-col cols="4">
            <div ref="polarEventChart"></div>
            <v-select
              class="mt-5"
              dense
              :items="areas"
              item-text="area"
              item-value="area"
              :label="$t('categories.nonconformity.area')"
              outlined
              :value="selectedArea"
              @change="onSelectedAreaChange"
            ></v-select>
          </v-col>
          <v-col cols="4">
            <div ref="polarOtherChart"></div>
            <v-select
              class="mt-5"
              dense
              :items="events"
              item-text="event"
              item-value="event"
              :label="$t('categories.nonconformity.event')"
              outlined
              :value="selectedEvent"
              @change="onSelectedEventChange"
            ></v-select>
          </v-col>
        </div>
      </v-card-text>
    </template>
  </avs-card>
</template>

<script>
import { makeChartOptions, makeChartObj, updateChartObj } from "@/utils/charts"

export default {
  name: "PolarNCChart",
  props: {
    inspections: {
      type: Array
    }
  },
  data() {
    return {
      charts: {},
      isChartTypePie: true,
      selectedArea: null,
      selectedEvent: null,
      selectedInspection: null
    }
  },
  computed: {
    isThemeDark() {
      return this.$store.state.app.isThemeDark
    },
    chartType() {
      return this.isChartTypePie
        ? { name: "pie", icon: "mdi-chart-bar", tooltip: "Switch to a bar chart" }
        : { name: "bar", icon: "mdi-chart-pie", tooltip: "Switch to a pie chart" }
    },
    areas() {
      return this.selectedInspection?.datas.areas || []
    },
    events() {
      return this.selectedInspection?.datas.events[this.selectedArea] || []
    },
    others() {
      const key = `${this.selectedArea}_${this.selectedEvent}`
      return this.selectedInspection?.datas.others[key] || []
    }
  },
  methods: {
    formatLabels(value) {
      return this.isChartTypePie ? parseFloat(value).toFixed(2) + "%" : value
    },
    formatSeries(series) {
      return this.isChartTypePie ? series : [{ data: series }]
    },
    getChartOptions(data, keyLabels, keySeries, title) {
      const [series, labels] = this.unzipObjectList(data, [keySeries, keyLabels])
      return makeChartOptions({
        chart: {
          height: 400,
          type: this.chartType.name
        },
        dataLabels: {
          enabled: true,
          formatter: this.formatLabels
        },
        labels: labels,
        isThemeDark: this.isThemeDark,
        series: this.formatSeries(series),
        titleText: title,
        xaxis: {
          labels: {
            rotate: 0,
            rotateAlways: false,
            hideOverlappingLabels: true
          }
        }
      })
    },
    makeOrUpdateChart(name, reference, options) {
      if (!this.charts[name]) {
        this.charts[name] = makeChartObj(reference, options)
      } else {
        updateChartObj(this.charts[name], options)
      }
    },
    onChartTypeChange(newValue) {
      this.isChartTypePie = newValue
      this.upChartArea()
      this.upChartEvent()
      this.upChartOther()
    },
    onSelectedInspectionChange(newValue) {
      this.selectedInspection = newValue
      this.onSelectedAreaChange(this.areas[0]?.area || null)
      this.upChartArea()
    },
    onSelectedAreaChange(newValue) {
      this.selectedArea = newValue
      this.onSelectedEventChange(this.events[0]?.event || null)
      this.upChartEvent()
    },
    onSelectedEventChange(newValue) {
      this.selectedEvent = newValue
      this.upChartOther()
    },
    unzipObjectList(data, keys) {
      return keys.map(key => data.map(e => e[key]))
    },
    upChartArea() {
      const options = this.getChartOptions(this.areas, "area", "counts", "Area")
      this.makeOrUpdateChart("area", this.$refs.polarAreaChart, options)
    },
    upChartEvent() {
      const options = this.getChartOptions(this.events, "event", "counts", "Event")
      this.makeOrUpdateChart("event", this.$refs.polarEventChart, options)
    },
    upChartOther() {
      const options = this.getChartOptions(this.others, "other", "counts", "Other")
      this.makeOrUpdateChart("other", this.$refs.polarOtherChart, options)
    }
  },
  watch: {
    inspections: {
      immediate: true,
      handler(newValue, oldValue) {
        this.$nextTick(() => {
          this.onSelectedInspectionChange(newValue[0] || null)
        })
      }
    },
    isThemeDark: {
      handler() {
        this.onSelectedInspectionChange(this.inspections[0])
      }
    }
  }
}
</script>

<style scoped>
.active {
  background: red;
}
</style>
